/**
 * Cancellations & Refunds Report Tab
 * View and manage order cancellations, refunds, and revenue adjustments
 */
import React, { useState, useCallback, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { 
  XCircle, RefreshCw, DollarSign, Users, Package, Calendar,
  TrendingDown, AlertTriangle, FileText, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/utils/apiClient';

export default function CancellationsReportTab() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expandedSection, setExpandedSection] = useState('recent');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const query = params.toString() ? `?${params.toString()}` : '';

      const res = await api.get(`/admin/cancellations/report${query}`);
      setReport(res.data);
    } catch (err) {
      toast.error('Error fetching report');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-FJ', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    }).format(val || 0);
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="text-sm text-muted-foreground">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40"
              />
            </div>
            <Button onClick={fetchReport} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {report && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-800 rounded-lg">
                  <TrendingDown className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Revenue Lost</p>
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                    ${formatCurrency(report.summary.total_revenue_lost)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-800 rounded-lg">
                  <XCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Cancellations</p>
                  <p className="text-2xl font-bold">{report.summary.total_cancellations}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-800 rounded-lg">
                  <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">VAT Recovered</p>
                  <p className="text-2xl font-bold">${formatCurrency(report.summary.vat_lost)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-800 rounded-lg">
                  <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Royalty Saved</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    ${formatCurrency(report.summary.royalty_saved)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Breakdown Sections */}
      {report && (
        <div className="space-y-4">
          {/* By Type */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Breakdown by Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-muted rounded-lg text-center">
                  <p className="text-3xl font-bold">{report.summary.order_cancellations}</p>
                  <p className="text-sm text-muted-foreground">Order Cancellations</p>
                </div>
                <div className="p-4 bg-muted rounded-lg text-center">
                  <p className="text-3xl font-bold">{report.summary.coffee_pass_cancellations}</p>
                  <p className="text-sm text-muted-foreground">Coffee Pass Cancellations</p>
                </div>
                <div className="p-4 bg-muted rounded-lg text-center">
                  <p className="text-3xl font-bold">{report.summary.refunds}</p>
                  <p className="text-sm text-muted-foreground">Refunds</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* By Customer */}
          <Card>
            <CardHeader 
              className="cursor-pointer" 
              onClick={() => toggleSection('customers')}
            >
              <CardTitle className="text-lg flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  By Customer
                </span>
                {expandedSection === 'customers' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardTitle>
            </CardHeader>
            <AnimatePresence>
              {expandedSection === 'customers' && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                >
                  <CardContent>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {Object.entries(report.by_customer).map(([key, val]) => (
                        <div key={key} className="flex justify-between items-center p-2 bg-muted rounded">
                          <div>
                            <p className="font-medium">{val.name}</p>
                            <p className="text-xs text-muted-foreground">{key}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-red-600 dark:text-red-400">
                              ${formatCurrency(val.total_refunded)}
                            </p>
                            <p className="text-xs text-muted-foreground">{val.cancellation_count} cancellations</p>
                          </div>
                        </div>
                      ))}
                      {Object.keys(report.by_customer).length === 0 && (
                        <p className="text-center text-muted-foreground py-4">No customer data</p>
                      )}
                    </div>
                  </CardContent>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>

          {/* By Item */}
          <Card>
            <CardHeader 
              className="cursor-pointer" 
              onClick={() => toggleSection('items')}
            >
              <CardTitle className="text-lg flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  By Item (Inventory Restored)
                </span>
                {expandedSection === 'items' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardTitle>
            </CardHeader>
            <AnimatePresence>
              {expandedSection === 'items' && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                >
                  <CardContent>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {Object.entries(report.by_item).map(([item, data]) => (
                        <div key={item} className="flex justify-between items-center p-2 bg-muted rounded">
                          <p className="font-medium">{item}</p>
                          <div className="text-right">
                            <p className="font-medium">{data.quantity} units</p>
                            <p className="text-xs text-red-600 dark:text-red-400">
                              ${formatCurrency(data.revenue_lost)} lost
                            </p>
                          </div>
                        </div>
                      ))}
                      {Object.keys(report.by_item).length === 0 && (
                        <p className="text-center text-muted-foreground py-4">No item data</p>
                      )}
                    </div>
                  </CardContent>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>

          {/* By Reason */}
          <Card>
            <CardHeader 
              className="cursor-pointer" 
              onClick={() => toggleSection('reasons')}
            >
              <CardTitle className="text-lg flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  By Reason
                </span>
                {expandedSection === 'reasons' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardTitle>
            </CardHeader>
            <AnimatePresence>
              {expandedSection === 'reasons' && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                >
                  <CardContent>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {Object.entries(report.by_reason).map(([reason, data]) => (
                        <div key={reason} className="flex justify-between items-center p-2 bg-muted rounded">
                          <p className="font-medium">{reason}</p>
                          <div className="text-right">
                            <p className="font-medium">{data.count} times</p>
                            <p className="text-xs text-red-600 dark:text-red-400">
                              ${formatCurrency(data.total)}
                            </p>
                          </div>
                        </div>
                      ))}
                      {Object.keys(report.by_reason).length === 0 && (
                        <p className="text-center text-muted-foreground py-4">No cancellations yet</p>
                      )}
                    </div>
                  </CardContent>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>

          {/* Recent Cancellations */}
          <Card>
            <CardHeader 
              className="cursor-pointer" 
              onClick={() => toggleSection('recent')}
            >
              <CardTitle className="text-lg flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Recent Cancellations
                </span>
                {expandedSection === 'recent' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardTitle>
            </CardHeader>
            <AnimatePresence>
              {expandedSection === 'recent' && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                >
                  <CardContent>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {report.recent_cancellations.map((c, i) => (
                        <div key={i} className="p-3 bg-muted rounded-lg">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-0.5 text-xs rounded ${
                                  c.type === 'order' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' :
                                  c.type === 'coffee_pass' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' :
                                  'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
                                }`}>
                                  {c.type === 'coffee_pass' ? 'Coffee Pass' : c.type}
                                </span>
                                <span className="text-xs text-muted-foreground">{c.cancellation_id}</span>
                              </div>
                              <p className="font-medium mt-1">
                                {c.customer_name || c.member_name || 'Unknown Customer'}
                              </p>
                              <p className="text-sm text-muted-foreground">{c.reason}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                By {c.cancelled_by_name} on {new Date(c.cancelled_at).toLocaleString()}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-red-600 dark:text-red-400">
                                ${formatCurrency(c.refund_amount)}
                              </p>
                              {c.refund_method && (
                                <p className="text-xs text-muted-foreground">{c.refund_method}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {report.recent_cancellations.length === 0 && (
                        <p className="text-center text-muted-foreground py-8">
                          No cancellations or refunds yet
                        </p>
                      )}
                    </div>
                  </CardContent>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        </div>
      )}

      {!report && !loading && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No report data available
          </CardContent>
        </Card>
      )}
    </div>
  );
}
